// src/context/AuthContext.js
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

/**
 * AuthContext - Manages user authentication and session state
 *
 * DEVELOPER GUIDE:
 * - After updating user profile on the server, ALWAYS call refreshUser()
 * - This ensures navbar, profile, and all components show updated data
 * - refreshUser() fetches fresh data from the API
 * - updateUser() is for local-only updates (use sparingly)
 *
 * Example:
 * const { refreshUser } = useAuth();
 * await fetch('/api/users/123', { method: 'PUT', body: ... });
 * await refreshUser(); // <-- Critical! Updates app-wide user state
 */

const AuthContext = createContext();

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

// Use SAME-ORIGIN routes everywhere. CRA proxy handles dev.
// cookies require credentials: "include"
async function apiFetch(path, options = {}) {
  const API_BASE = process.env.REACT_APP_API_BASE || "";

  // If a separate API origin is configured (common in production), prefix /api/* paths.
  const rawPath = String(path || "");
  const url =
    API_BASE && rawPath.startsWith("/api/") ? `${API_BASE}${rawPath}` : rawPath;

  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
    },
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const msg =
      typeof data === "string"
        ? data
        : data?.error || data?.message || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function normalizeUserFromLogin(data) {
  const u = data?.user || data?.data?.user || data || {};
  return {
    id: u.user_id ?? u.id,
    email: u.email,
    username: u.username,
    bio: u.bio || "",
    profile_picture: u.profile_picture || "/avatars/avatar1.png",
    banner_image: u.banner_image || null,
    tos_accepted: Boolean(u.tos_accepted),
    real_email: u.real_email ?? null,
    real_email_verified: Boolean(u.real_email_verified),
  };
}

function normalizeUserFromGet(data, fallback) {
  const u = data?.user || data?.data?.user || data || {};
  return {
    id: u.user_id ?? u.id ?? fallback?.id,
    email: u.email ?? fallback?.email,
    username: u.username ?? fallback?.username,
    bio: u.bio ?? fallback?.bio ?? "",
    profile_picture: u.profile_picture ?? fallback?.profile_picture,
    banner_image: u.banner_image ?? fallback?.banner_image ?? null,
    tos_accepted:
      u.tos_accepted !== undefined
        ? Boolean(u.tos_accepted)
        : fallback?.tos_accepted,
    real_email:
      u.real_email !== undefined ? u.real_email : fallback?.real_email ?? null,
    real_email_verified:
      u.real_email_verified !== undefined
        ? Boolean(u.real_email_verified)
        : fallback?.real_email_verified ?? false,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  const clearSessionLocal = useCallback(() => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("lastActivity");
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
  }, []);

  // Check session using same-origin
  const checkSession = useCallback(async () => {
    try {
      await apiFetch("/api/users/session");
      return true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/users/logout", { method: "POST" });
    } catch {
      // still clear local state
    } finally {
      clearSessionLocal();
    }
  }, [clearSessionLocal]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    localStorage.setItem("lastActivity", Date.now().toString());

    inactivityTimerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    const handleActivity = () => resetInactivityTimer();

    events.forEach((evt) => document.addEventListener(evt, handleActivity));
    resetInactivityTimer();

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, handleActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    const initSession = async () => {
      setAuthLoading(true);
      const storedUser = localStorage.getItem("user");
      const lastActivity = localStorage.getItem("lastActivity");

      if (!storedUser) {
        setAuthLoading(false);
        return;
      }

      // Optimistically hydrate UI from localStorage immediately.
      // If the server session check fails, we'll log out and clear it.
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        await logout();
        setAuthLoading(false);
        return;
      }

      // inactivity timeout
      if (lastActivity) {
        const timeSince = Date.now() - parseInt(lastActivity, 10);
        if (Number.isFinite(timeSince) && timeSince > INACTIVITY_TIMEOUT) {
          await logout();
          setAuthLoading(false);
          return;
        }
      }

      const isValid = await checkSession();
      if (!isValid) {
        await logout();
      }

      setAuthLoading(false);
    };

    initSession().catch(() => {
      clearSessionLocal();
      setAuthLoading(false);
    });
  }, [checkSession, logout]);

  const login = useCallback(async (email, password) => {
    const data = await apiFetch("/api/users/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const userData = normalizeUserFromLogin(data);

    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("lastActivity", Date.now().toString());
  }, []);

  const register = useCallback(
    async (email, password, username, recaptchaToken) => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const data = await apiFetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          username,
          recaptchaToken,
          timezone,
        }),
      });

      const userData = normalizeUserFromLogin(data);

      const safe = {
        ...userData,
        profile_picture: userData.profile_picture || "/avatars/avatar1.png",
        bio: userData.bio || "",
      };

      setUser(safe);
      localStorage.setItem("user", JSON.stringify(safe));
      localStorage.setItem("lastActivity", Date.now().toString());
    },
    []
  );

  const updateUser = useCallback((newData) => {
    setUser((prev) => {
      const updated = { ...(prev || {}), ...(newData || {}) };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!user?.id) return;

    try {
      const data = await apiFetch(`/api/users/${user.id}`);
      const userData = normalizeUserFromGet(data, user);

      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    } catch (error) {
      if (error?.status === 401) {
        clearSessionLocal();
      } else {
        console.error("Failed to refresh user data:", error);
      }
    }
  }, [user, clearSessionLocal]);

  const isAuthenticated = Boolean(user?.id);

  return (
    <AuthContext.Provider
      value={{
        user,
        authLoading,
        isAuthenticated,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
